import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import {
  ColumnConfig,
  DataTable,
  DataTablePagination,
  defineTableSchema,
  useConfiguredTable,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  AddFilterTrigger,
  FilterBar,
  FilterEditor,
  useFilters,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/filters';
import { useQuery } from '@tanstack/react-query';
import { CircleAlert, UserIcon } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import service from '../../../service';
import { TicketedEventTag } from '../../../types/ticketedEvents';

const fallbackData = [];

const columnConfigs: ColumnConfig<TicketedEventTag>[] = [
  {
    id: 'name',
    headerLabel: 'Name',
    accessor: (row) => row.name ?? 'N/A',
    type: 'text',
  },
  {
    id: 'description',
    headerLabel: 'Description',
    accessor: (row) => row.description ?? 'N/A',
    type: 'text',
  },
];

type TicketedEventTagsListProps = {
  onRowClick?: (row: { original: TicketedEventTag }) => void;
};

export default function TicketedEventTagsList({
  onRowClick,
}: TicketedEventTagsListProps = {}) {
  const navigate = useNavigate();
  const { filters, addOrUpdateFilter, removeFilter } = useFilters([]);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  // const [columnPinning, setColumnPinning] = useState({
  //   left: ['select'],
  //   right: [],
  // });

  const { columns, filters: filterDefinitions } =
    defineTableSchema<TicketedEventTag>(columnConfigs);

  const { data, status } = useQuery<TicketedEventTag[]>({
    queryKey: ['ticketed-event-tags'],
    queryFn: async () => service.getTicketedEventTags(),
  });

  const { table } = useConfiguredTable({
    data: data ?? fallbackData,
    columns,
    filters,
    // initialSorting: [{ id: sortBy, desc: true }],
    pagination: {
      pageIndex,
      pageSize,
      setPageIndex,
      setPageSize,
    },
    // columnPinning,
    // setColumnPinning,
    autoResetPageOnFilterChange: true,
  });

  return (
    <div>
      <div className="mt-2 space-y-4">
        <div className="flex h-16 w-full items-center gap-4 rounded-lg bg-zinc-100 p-4">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-200">
              <UserIcon className="h-4 w-4 text-zinc-500" />
            </div>
            <div>
              Tags: <span className="font-bold">{data ? data.length : 0}</span>
            </div>
          </div>
        </div>

        <FilterBar>
          {filters.map((f) => {
            const def = filterDefinitions.find((d) => d.field === f.field);
            if (!def) return null;
            return (
              <FilterEditor
                key={f.field}
                field={f.field}
                displayName={f.displayName}
                filterItem={f}
                inputType={def.type}
                operators={def.operators}
                applyFilter={addOrUpdateFilter}
                removeFilter={removeFilter}
                description={def.description}
              />
            );
          })}

          <AddFilterTrigger
            availableFilters={filterDefinitions.filter(
              (def) => !filters.some((f) => f.field === def.field),
            )}
            onAdd={(newFilter) => addOrUpdateFilter(newFilter)}
          />
        </FilterBar>

        {status === 'error' && (
          <Alert variant="destructive" className="my-4">
            <CircleAlert />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Error loading tags. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        {status !== 'error' && (
          <>
            <DataTable
              table={table}
              onRowClick={
                onRowClick ??
                ((row) => navigate(`/ticketed-events/${row.original.id}`))
              }
              isLoading={status === 'pending'}
              skeletonRowCount={pageSize}
            />
            <DataTablePagination
              table={table}
              meta={{ total: table.getFilteredRowModel()?.rows?.length }}
              pagination={{ size: pageSize, number: pageIndex + 1 }}
              setPage={({ size, number }) => {
                setPageSize(size);
                setPageIndex(number - 1);
              }}
              setPageNumber={(n) => setPageIndex(n - 1)}
            />
          </>
        )}
      </div>
    </div>
  );
}
